import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useBlocker } from "react-router-dom";
import { useTranslation } from "react-i18next";

type UnsavedChangesContextValue = {
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
  /** Confirma saída se houver alterações; false = cancelar navegação. */
  confirmLeave: () => boolean;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue>({
  isDirty: false,
  setDirty: () => undefined,
  confirmLeave: () => true,
});

/**
 * Guarda formulário sujo no SPA (LAC-078.A).
 * useBlocker (data router) + beforeunload — cobre menu interno e fechar aba.
 */
export function UnsavedChangesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [isDirty, setIsDirty] = useState(false);

  const setDirty = useCallback((dirty: boolean) => {
    setIsDirty(dirty);
  }, []);

  const confirmLeave = useCallback(() => {
    if (!isDirty) {
      return true;
    }
    return window.confirm(t("settings.account.unsavedLeave"));
  }, [isDirty, t]);

  // Bloqueia navigate/Link do React Router quando Conta (ou outra tela) está suja.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );

  useEffect(() => {
    if (blocker.state !== "blocked") {
      return;
    }
    if (window.confirm(t("settings.account.unsavedLeave"))) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, t]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const value = useMemo(
    () => ({ isDirty, setDirty, confirmLeave }),
    [isDirty, setDirty, confirmLeave]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges(): UnsavedChangesContextValue {
  return useContext(UnsavedChangesContext);
}
